import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Mail, Loader2, Copy, ExternalLink, Smartphone, X } from 'lucide-react';
import { toast } from 'sonner';
import { cloudFunctions } from '@/lib/firebase';
import type { Site, Lead } from '@shared/types';

const DEFAULT_SMS_MESSAGE = `Hi {{businessName}}! I built a free website preview just for your business. Check it out here: {{siteUrl}} - No obligation, just wanted to show you what's possible!`;

export interface OutreachModalProps {
  site: Site & { id: string };
  lead: (Lead & { id: string }) | null;
  isLoadingLead: boolean;
  onClose: (markAsSent?: boolean) => void;
  readOnly?: boolean;
}

export function OutreachModal({ site, lead, isLoadingLead, onClose, readOnly }: OutreachModalProps) {
  const [smsMessage, setSmsMessage] = useState(DEFAULT_SMS_MESSAGE);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [customPrice, setCustomPrice] = useState<string>(site.price ? String(site.price / 100) : '299');
  const [savingPrice, setSavingPrice] = useState(false);
  const priceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Determine available contact methods - SMS only
  const hasPhone = !!lead?.phone;
  const canContact = hasPhone;

  // Always use SMS method
  const contactMethod: 'sms' | null = hasPhone ? 'sms' : null;

  // Generate preview URL when modal opens (only if lead can be contacted).
  // We prefer the configured public domain (e.g. https://romulus.app) so the
  // URL we show the business is always the clean branded one - regardless
  // of whether the seller happens to be using pagepilot-cad7f.web.app or
  // localhost. Falls back to the current origin if the env var is unset.
  const publicBaseUrl =
    (import.meta.env.VITE_PUBLIC_DOMAIN as string | undefined)?.replace(/\/$/, '') ||
    window.location.origin;

  useEffect(() => {
    if (canContact && site.id && !previewUrl && !isGeneratingPreview) {
      setIsGeneratingPreview(true);
      cloudFunctions.getPreviewShareLink({ siteId: site.id })
        .then(({ token }) => {
          setPreviewUrl(`${publicBaseUrl}/p/${token}`);
        })
        .catch((err) => {
          console.error('Failed to generate preview URL:', err);
          toast.error('Failed to generate preview link');
        })
        .finally(() => {
          setIsGeneratingPreview(false);
        });
    }
  }, [canContact, site.id, previewUrl, isGeneratingPreview]);

  const getPreviewText = (text: string) => {
    if (!lead) return text;
    const ownerName = lead.businessName.split(' ')[0];
    const siteUrl = previewUrl || '';

    return text
      .replace(/\{\{businessName\}\}/g, lead.businessName)
      .replace(/\{\{ownerName\}\}/g, ownerName)
      .replace(/\{\{siteUrl\}\}/g, siteUrl)
      .replace(/\{\{#if siteUrl\}\}([\s\S]*?)\{\{\/if\}\}/g, siteUrl ? '$1' : '');
  };


  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const handlePriceChange = (newPrice: string) => {
    setCustomPrice(newPrice);
    const priceNum = parseFloat(newPrice);
    if (isNaN(priceNum) || priceNum < 1) return;

    // Debounce: wait 800ms after user stops typing before saving
    clearTimeout(priceTimeoutRef.current);
    setSavingPrice(true);
    priceTimeoutRef.current = setTimeout(async () => {
      try {
        await cloudFunctions.updateSiteContent({
          siteId: site.id,
          content: {},
          price: Math.round(priceNum * 100), // Convert to cents
        });
      } catch (err) {
        console.error('Failed to save price:', err);
        toast.error('Failed to save price');
      } finally {
        setSavingPrice(false);
      }
    }, 800);
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(false); }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10100,
        padding: '20px',
      }}>
      <div style={{
        background: 'rgba(20,20,20,0.98)',
        border: '1px solid rgba(91,58,58,0.3)',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid rgba(91,58,58,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #B08080, #c49494)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {contactMethod === 'sms' ? (
                <MessageSquare style={{ width: '20px', height: '20px', color: '#FFFBFE' }} />
              ) : (
                <Mail style={{ width: '20px', height: '20px', color: '#FFFBFE' }} />
              )}
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFBFE', margin: 0 }}>
                {contactMethod === 'sms' ? 'Send Text to Business' : 'Send Email to Business'}
              </h2>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
                {site.name}
              </p>
            </div>
          </div>
          <button
              onClick={() => onClose(false)}
              style={{
                padding: '8px',
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X style={{ width: '18px', height: '18px' }} />
            </button>
        </div>


        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {isLoadingLead ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
              <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: '12px' }}>Loading lead information...</p>
            </div>
          ) : !lead ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
              <p>Lead not found</p>
              <button
                onClick={() => onClose(false)}
                style={{
                  marginTop: '16px',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent',
                  color: '#FFFBFE',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Close
              </button>
            </div>
          ) : !canContact ? (
            <div style={{
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: '8px',
              padding: '16px',
              textAlign: 'center',
            }}>
              <p style={{ color: '#B08080', margin: '0 0 16px' }}>
                This lead has no contact method available (no email or phone number).
              </p>
              <button
                onClick={() => onClose(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent',
                  color: '#FFFBFE',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Close
              </button>
            </div>
          ) : isGeneratingPreview ? (
            /* Show only loading state while generating preview */
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              textAlign: 'center',
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'rgba(91, 58, 58, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
              }}>
                <Loader2 style={{ width: '32px', height: '32px', color: '#B08080', animation: 'spin 1s linear infinite' }} />
              </div>
              <h3 style={{ color: '#FFFBFE', fontSize: '18px', margin: '0 0 8px', fontWeight: 600 }}>
                Generating Preview Link
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0 }}>
                Please wait while we prepare your preview link...
              </p>
            </div>
          ) : (
            <>
              {/* Recipient Info */}
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>To:</span>
                  <span style={{ color: '#FFFBFE', fontSize: '14px' }}>
                    {contactMethod === 'sms' ? lead.phone : lead.email}
                  </span>
                  <span style={{
                    background: contactMethod === 'sms' ? 'rgba(136, 136, 136, 0.2)' : 'rgba(91, 58, 58, 0.2)',
                    color: contactMethod === 'sms' ? '#888888' : '#B08080',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}>
                    {contactMethod === 'sms' ? 'SMS' : 'Email'}
                  </span>
                  <span style={{
                    background: 'rgba(91, 58, 58, 0.2)',
                    color: '#B08080',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}>
                    {lead.businessName}
                  </span>
                </div>
              </div>

              {/* Giant Price Setter - Centered (hidden in read-only mode) */}
              {!readOnly && <div style={{
                background: 'rgba(91, 58, 58, 0.1)',
                border: '1px solid rgba(91, 58, 58, 0.2)',
                borderRadius: '16px',
                padding: '32px 24px',
                marginBottom: '20px',
                textAlign: 'center',
              }}>
                <p style={{ fontSize: '16px', color: '#FFFBFE', margin: '0 0 8px', fontWeight: 600 }}>
                  Set Website Price
                </p>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: '0 0 20px' }}>
                  You keep 100% of every sale
                </p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span style={{ color: '#B08080', fontSize: '48px', fontWeight: 700 }}>$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={customPrice}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value === '' || parseInt(value, 10) <= 9999) {
                        handlePriceChange(value);
                      }
                    }}
                    style={{
                      width: '160px',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      border: '2px solid rgba(91, 58, 58, 0.4)',
                      background: 'rgba(0,0,0,0.4)',
                      color: '#FFFBFE',
                      fontSize: '48px',
                      fontWeight: 700,
                      textAlign: 'center',
                      outline: 'none',
                    }}
                  />
                  {savingPrice && (
                    <Loader2 style={{ width: '24px', height: '24px', color: '#B08080', animation: 'spin 1s linear infinite' }} />
                  )}
                </div>
              </div>}

              {/* Purchase Link Info - Under price setter */}
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
              }}>
                <p style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.7)',
                  margin: 0,
                  textAlign: 'center',
                }}>
                  The message includes a purchase link. When the business owner clicks it, they can preview and buy the website directly. The sale is handled entirely on their end - you'll receive your payment automatically.
                </p>
              </div>

              {/* SMS Outreach - Copy & Paste */}
              <>
                {/* QR Code Section - First thing, prominently displayed */}
                  <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    textAlign: 'center',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                    }}>
                      <Smartphone style={{ width: '18px', height: '18px', color: '#888888' }} />
                      <p style={{
                        fontSize: '14px',
                        color: '#888888',
                        margin: 0,
                        fontWeight: 500,
                      }}>
                        Scan to Send from Phone
                      </p>
                    </div>
                    <p style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.5)',
                      margin: '0 0 16px',
                    }}>
                      Scan this QR code with your phone camera to open the message with pre-filled text
                    </p>

                    {/* QR Code */}
                    <div style={{
                      display: 'inline-block',
                      background: 'white',
                      padding: '12px',
                      borderRadius: '12px',
                    }}>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`sms:${lead?.phone}?body=${encodeURIComponent(getPreviewText(smsMessage))}`)}`}
                        alt="QR Code to send SMS"
                        style={{
                          width: '180px',
                          height: '180px',
                          display: 'block',
                        }}
                      />
                    </div>

                    <p style={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.4)',
                      marginTop: '12px',
                    }}>
                      Message personalized for: <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{lead?.businessName}</strong>
                    </p>
                  </div>

                  {/* Editable Message with Copy - Combined edit and preview */}
                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '16px',
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                    }}>
                      <p style={{
                        fontSize: '13px',
                        color: 'rgba(255,255,255,0.7)',
                        margin: 0,
                        fontWeight: 500,
                      }}>
                        Message
                      </p>
                      <button
                        onClick={() => copyToClipboard(getPreviewText(smsMessage), 'Message')}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid rgba(136, 136, 136, 0.3)',
                          background: 'rgba(136, 136, 136, 0.1)',
                          color: '#888888',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '13px',
                        }}
                      >
                        <Copy style={{ width: '14px', height: '14px' }} />
                        Copy Message
                      </button>
                    </div>
                    <textarea
                      value={getPreviewText(smsMessage)}
                      onChange={(e) => {
                        const newText = e.target.value;
                        setSmsMessage(newText);
                      }}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(0,0,0,0.5)',
                        color: '#FFFBFE',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        lineHeight: '1.6',
                      }}
                    />
                  </div>

                  {/* Quick Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                  }}>
                    <a
                      href={`sms:${lead?.phone}?body=${encodeURIComponent(getPreviewText(smsMessage))}`}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(to right, #888888, #888888)',
                        color: '#FFFBFE',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        textDecoration: 'none',
                        fontWeight: 500,
                      }}
                    >
                      <ExternalLink style={{ width: '16px', height: '16px' }} />
                      Open in Messages App
                    </a>
                  </div>
              </>
            </>
          )}
        </div>

        {/* SMS Footer */}
        {canContact && contactMethod === 'sms' && !isGeneratingPreview && (
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid rgba(91,58,58,0.15)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
            <button
              onClick={() => onClose(readOnly ? false : true)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: readOnly ? '1px solid rgba(255,255,255,0.2)' : 'none',
                background: readOnly ? 'transparent' : 'linear-gradient(135deg, #B08080, #c49494)',
                color: '#FFFBFE',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {readOnly ? 'Close' : 'Done - Mark as Sent'}
            </button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
